import * as React from "react";
import { PublicClientApplication, type AccountInfo } from "@azure/msal-browser";

const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined;
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined;
const API_SCOPE = import.meta.env.VITE_ENTRA_API_SCOPE as string | undefined;

const MSAL_ENABLED = Boolean(TENANT_ID && CLIENT_ID && API_SCOPE);

export const msalInstance = MSAL_ENABLED
  ? new PublicClientApplication({
      auth: {
        clientId: CLIENT_ID!,
        authority: `https://login.microsoftonline.com/${TENANT_ID}`,
        redirectUri: window.location.origin,
      },
      // Bewusst sessionStorage statt localStorage: das MSAL-Konto/Token wird beim Schließen des
      // Tabs verworfen (kein Login-Überdauern über Tabs/Browser-Neustarts hinweg) - eine
      // deliberate Sicherheitsentscheidung zugunsten eines kleineren Zeitfensters für gestohlene
      // Tokens, nicht ein Versehen. Konsequenz: jeder neue Tab erfordert eine erneute Anmeldung.
      cache: { cacheLocation: "sessionStorage" },
    })
  : null;

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
  // ISO-Zeitstempel wenn der Benutzer in der Administration gesperrt wurde. Backend liefert null,
  // wenn nicht gesperrt. Wird in App.tsx ausgewertet, um statt der Anwendung die Sperrseite zu
  // rendern.
  blockedAt?: string | null;
}

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  isMsalEnabled: boolean;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

async function acquireToken(account: AccountInfo): Promise<string | null> {
  if (!msalInstance) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({ account, scopes: [API_SCOPE!] });
    return result.accessToken;
  } catch {
    const result = await msalInstance.acquireTokenPopup({ scopes: [API_SCOPE!] });
    return result.accessToken;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = React.useState<AccountInfo | null>(null);
  const [user, setUser] = React.useState<CurrentUser | null>(null);
  const [loading, setLoading] = React.useState(true);

  const getAccessToken = React.useCallback(async () => {
    if (!MSAL_ENABLED || !account) return null;
    return acquireToken(account);
  }, [account]);

  const fetchMe = React.useCallback(async (token: string | null) => {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL ?? ""}/api/users/me`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (response.ok) {
      const body = await response.json();
      setUser(body.data);
    }
  }, []);

  const login = React.useCallback(async () => {
    if (!msalInstance) return;
    const result = await msalInstance.loginPopup({ scopes: [API_SCOPE!] });
    setAccount(result.account);
    // Ohne diesen Aufruf blieb "user" nach einem frischen Popup-Login null, obwohl MSAL bereits
    // ein gültiges Konto/Token besitzt - App() zeigt dann fälschlich weiterhin LoginGate an, bis
    // ein manueller Seiten-Refresh den init()-Effekt erneut ausführt (siehe init() unten, das
    // denselben fetchMe()-Aufruf beim Mount macht). Direkt hier nachziehen behebt das.
    await fetchMe(result.accessToken);
  }, [fetchMe]);

  const logout = React.useCallback(() => {
    if (!msalInstance) return;
    void msalInstance.logoutRedirect();
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    async function init() {
      if (!MSAL_ENABLED) {
        // Lokaler Entwicklungsmodus: Server akzeptiert den Aufruf über DEV_AUTH_ENABLED (siehe .env.example).
        await fetchMe(null);
        if (!cancelled) setLoading(false);
        return;
      }
      await msalInstance!.initialize();
      const result = await msalInstance!.handleRedirectPromise().catch(() => null);
      const activeAccount = result?.account ?? msalInstance!.getAllAccounts()[0] ?? null;
      if (activeAccount) {
        setAccount(activeAccount);
        const token = await acquireToken(activeAccount).catch(() => null);
        await fetchMe(token);
      }
      if (!cancelled) setLoading(false);
    }
    void init();
    return () => {
      cancelled = true;
    };
  }, [fetchMe]);

  const value: AuthContextValue = { user, loading, isMsalEnabled: MSAL_ENABLED, login, logout, getAccessToken };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth muss innerhalb von AuthProvider verwendet werden.");
  return ctx;
}
